import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardTitle,
  CardSubtitle,
  CardText,
  CardBody,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";

import { makeId } from "@/helpers/func";
import { url } from "@/store/initialStatus";
import { Translate } from "@/helpers/Translate";
import "./Resturant.css";

// Placeholder shown when a meal has no image
const NO_IMAGE_URL =
  "https://i.pinimg.com/originals/dd/9d/c9/dd9dc9d83423bc037b511d73b29e6b80.png";

const Image = React.memo(function Image(props: any) {
  return (
    <img
      src={props.src}
      width={props.width}
      alt={props.alt}
      className="spinner"
    />
  );
});

const t = new Translate();

/** Groups menu items by weekday. */
const itemsByDay = (items: any) => ({
  Saturday: items.filter((item: any) => item.day === "Saturday"),
  Sunday: items.filter((item: any) => item.day === "Sunday"),
  Monday: items.filter((item: any) => item.day === "Monday"),
  Tuesday: items.filter((item: any) => item.day === "Tuesday"),
  Wednesday: items.filter((item: any) => item.day === "Wednesday"),
  Thursday: items.filter((item: any) => item.day === "Thursday"),
});

/** Groups saved reservations by school day. */
const reservationsByDay = (items: any) => ({
  Sunday: items.filter((item: any) => item.day === "Sunday"),
  Monday: items.filter((item: any) => item.day === "Monday"),
  Tuesday: items.filter((item: any) => item.day === "Tuesday"),
  Wednesday: items.filter((item: any) => item.day === "Wednesday"),
  Thursday: items.filter((item: any) => item.day === "Thursday"),
});

/**
 * Weekly meal reservations.
 *
 * A table of school days x students; each cell lists the reserved meals as
 * removable chips and opens a picker modal to add / remove meals.
 * `reservation` is keyed by day: { [day]: [{ student_id, itemcode }] }.
 */
function Resturant() {
  interface Idays {
    Saturday: [];
    Sunday: [];
    Monday: [];
    Tuesday: [];
    Wednesday: [];
    Thursday: [];
  }

  const WeekDay = [
    t.translate("Sunday"),
    t.translate("Monday"),
    t.translate("Tuesday"),
    t.translate("Wednesday"),
    t.translate("Thursday"),
  ];

  const [StudentOptions, setStudentOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const Navigate = useNavigate();
  const [modal, setModal] = useState(false);
  const [DayMenu, SetDayMenu] = useState<any>(null);
  const [reservation, setReservation] = useState<any>({});
  const [filteredDays, setFilteredDays] = useState<Idays>();
  const [mealsOptions, setMealsOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const toggle = () => setModal(!modal);

  // Load the weekly menu, the students and any saved reservations.
  // The endpoint fails outside the reservation window -> back to home.
  useEffect(() => {
    axios
      .get(`${url}reservemeals/getutils.php`)
      .then((res: any) => {
        setFilteredDays(itemsByDay(res.data.data.items));

        const newStudentOptions = res.data.data.students.map((student: any) => {
          return {
            label: ` ${student.fullname} `,
            value: student.id,
          };
        });

        setStudentOptions(newStudentOptions);

        if (res.data.data.reservations) {
          setReservation(reservationsByDay(res.data.data.reservations));
        } else {
          const newMealOptions = res.data.data.items.map((meal: any) => {
            return {
              ...meal,
              label: `${meal.ItemCode ? meal.ItemCode : " "} - ${meal.ItemAname}`,
              value: meal.ItemCode,
            };
          });
          setMealsOptions(newMealOptions);
        }
      })
      .catch(() => {
        Swal.fire(
          t.translate(
            "You can make your weekly menu reservations  from Monday till Wednesday of every week"
          ),
          " ",
          "error"
        ).then(() => {
          Navigate("dashboard/home");
        });
      });
  }, [Navigate]);

  const Submit = () => {
    Swal.showLoading();
    axios
      .post(`${url}reservemeals/savemenu.php`, reservation)
      .then((res: any) => {
        if (res.data.data === "success") {
          Swal.fire("Saved Successfully!", "", "success");
        } else {
          Swal.fire(
            "Something go wrong please try again later !!",
            "",
            "error"
          );
        }
      });
  };

  const getIngredients = (itemsIds: any) => {
    axios.post(`${url}reservemeals/getingredients.php`, {
      itemsIds: itemsIds,
    });
  };

  /** Removes the reservation at index `i` of `day`. */
  const Clear = (day: any, i: any) => {
    if (!reservation[day] || i == -1) {
      return;
    } else {
      setReservation((prevState: any) => {
        let dayReservation = prevState[day];
        let obj = dayReservation.splice(i, 1);
        getIngredients(
          dayReservation.map((d: any) =>
            d.student_id == obj.student_id ? d.ItemCode : undefined
          )
        );
        prevState[day] = dayReservation;
        return { ...prevState };
      });
    }
  };

  /**
   * Builds the meal cards shown in the picker modal for one student / day.
   * Clicking a card toggles its reservation and rebuilds the cards.
   */
  const genModalMenu = (dayMenu: any, student_id: string, day: string) => {
    let m = dayMenu.map((item: any) => {
      let classN = "";

      if (reservation[day]) {
        let temp = reservation[day].find(
          (i: any) =>
            i.student_id === student_id && i.itemcode === item.ItemCode
        );
        if (temp) {
          classN = " activeCard ";
        }
      }
      return (
        <Card
          key={makeId(10)}
          className={" pointer " + classN}
          onClick={() => {
            let tempR = reservation;

            if (!tempR[day]) {
              tempR[day] = [
                { student_id: student_id, itemcode: item.ItemCode },
              ];
            } else {
              let x = tempR[day].findIndex(
                (i: any) =>
                  i.student_id === student_id && i.itemcode === item.ItemCode
              );
              if (x !== -1) {
                tempR[day].splice(x, 1);
              } else {
                tempR[day].push({
                  student_id: student_id,
                  itemcode: item.ItemCode,
                });
              }
            }
            setReservation(tempR);
            genModalMenu(dayMenu, student_id, day);
          }}
        >
          <Image
            src={
              item.imgurl == ""
                ? NO_IMAGE_URL
                : item.imgurl?.includes("https://")
                  ? item.imgurl
                  : `https://drive.google.com/uc?id=${item.imgurl}&export=download`
            }
            alt={item.ItemEname}
            width="100%"
          />
          <CardBody>
            <CardTitle tag="h5">{item.ItemEname}</CardTitle>
            <CardSubtitle tag="h6" className="mb-2 text-muted">
              {item.ItemAname}
            </CardSubtitle>
            <CardText>
              {t.translate("Price")}: {item.SellingPrice} {t.translate("L.E.")}
            </CardText>
          </CardBody>
        </Card>
      );
    });
    SetDayMenu(m);
  };

  /** Reserved meals of one student on one day, as removable chips. */
  const renderMealsCell = (day: any, options: { value: string; label: string }, DaysOptions: any) => {
    const chips = DaysOptions[day].map((Meal: any, i: any) => {
      let found = false;
      let index = -1;
      if (reservation[day]) {
        index = reservation[day].findIndex(
          (i: any) =>
            i.student_id === options.value && i.itemcode === Meal.ItemCode
        );
        if (index !== -1) {
          found = true;
        }
      }
      if (found)
        return (
          <span key={i + Meal.ItemCode} className="pp-cafe-chip">
            <span>{`${Meal.ItemEname}`}</span>
            <button
              type="button"
              className="pp-cafe-chip-remove"
              aria-label={`Remove ${Meal.ItemEname}`}
              onClick={() => Clear(day, index)}
            >
              &times;
            </button>
          </span>
        );
      return null;
    });

    return (
      <td>
        <div className="pp-cafe-chips">
          {chips}
          <button
            type="button"
            className="btn btn-sm btn-success"
            onClick={() => {
              genModalMenu(DaysOptions[day], options.value, day);
              toggle();
            }}
          >
            <i className="fas fa-plus" aria-hidden="true"></i>{" "}
            {t.translate("Select Meals")}
          </button>
        </div>
      </td>
    );
  };

  // Dates of next week's Sunday and Thursday (shown in the validity note)
  var Sunday = new Date();
  Sunday.setDate(Sunday.getDate() + ((5 + (7 - Sunday.getDay())) % 7) + 2);
  var Thursday = new Date();
  Thursday.setDate(
    Thursday.getDate() + ((5 + (7 - Thursday.getDay())) % 7) + 6
  );

  return (
    <React.Fragment>
      {/* Validity period of this week's selection */}
      <div className="pp-cafe-note">
        <i className="far fa-calendar-alt" aria-hidden="true"></i>
        <span>
          {`${t.translate("This selection is valid from")} ${t.translate(
            "Sunday"
          )} ${Sunday.toLocaleString().split(",")[0]} ${t.translate(
            "to"
          )} ${t.translate("Thursday")} ${Thursday.toLocaleString().split(",")[0]}`}
        </span>
      </div>

      {/* Meal picker */}
      <Modal isOpen={modal} toggle={toggle} className="modal-xl" scrollable>
        <ModalHeader toggle={toggle}>{t.translate("Meals")}</ModalHeader>
        <ModalBody>
          <div className="pp-cafe-menu-grid">{DayMenu}</div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              toggle();
            }}
          >
            {t.translate("Done")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Days x students */}
      <div className="pp-cafe-card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>{t.translate("Days")}</th>
                <th>{t.translate("Students")}</th>
                <th>{t.translate("Meals")}</th>
              </tr>
            </thead>
            <tbody>
              {WeekDay.map((day) => {
                let temp = StudentOptions
                  ? StudentOptions.map((options, i) => {
                      let DaysOptions: any = filteredDays ?? [];
                      return (
                        <tr key={day + options.label}>
                          {/* Day cell spans all student rows */}
                          {i == 0 && (
                            <td className="pp-cafe-day" rowSpan={StudentOptions.length}>
                              {day}
                            </td>
                          )}
                          <td className="pp-cafe-student">
                            {options.label}
                            <span className="pp-cafe-student-id">#{options.value}</span>
                          </td>
                          {renderMealsCell(day, options, DaysOptions)}
                        </tr>
                      );
                    })
                  : null;

                return temp;
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pp-cafe-save">
        <button type="button" className="btn btn-warning btn-lg" onClick={Submit}>
          <i className="fas fa-save" aria-hidden="true"></i>
          {t.translate("Save")}
        </button>
      </div>
    </React.Fragment>
  );
}

export default Resturant;
